import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { RevealMask } from "@/components/motion/Reveal";
import ScrambleText from "@/components/motion/ScrambleText";
import { capabilities } from "@/data/content";

export default function Capabilities() {
  return (
    <section data-testid="capabilities" className="border-y border-line bg-surface">
      <div className="mx-auto max-w-[1600px] px-5 py-20 sm:py-24 md:py-28 lg:py-36 xl:py-40 sm:px-8 md:px-10 lg:px-12 xl:px-16">
        <div className="mb-10 grid gap-6 sm:mb-12 sm:gap-8 md:mb-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <ScrambleText as="p" text="Capabilities" className="mb-4 block text-xs font-bold uppercase tracking-[0.22em] text-accent" />
            <RevealMask as="h2" className="font-display text-5xl font-extrabold leading-[0.95] tracking-tight text-ink md:text-7xl">
              Five disciplines.
            </RevealMask>
            <RevealMask as="h2" delay={0.08} className="font-display text-5xl font-extrabold leading-[0.95] tracking-tight text-ink md:text-7xl">
              One <span className="font-serif font-normal italic text-accent">integrated</span> team.
            </RevealMask>
          </div>
          <p className="self-end text-base leading-relaxed text-muted lg:col-span-4 lg:col-start-9">
            Most brands buy strategy, creative, media and PR from four different rooms. We put them in one — so the idea stays intact from insight to activation.
          </p>
        </div>

        <div className="border-t border-line">
          {capabilities.map((cap, i) => (
            <motion.div
              key={cap.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.7, delay: i * 0.05, ease: [0.76, 0, 0.24, 1] }}
              className="group grid grid-cols-1 gap-4 border-b border-line py-10 transition-colors duration-500 hover:bg-bone/70 md:grid-cols-12 md:gap-8 md:px-4"
            >
              <span className="font-serif text-2xl italic text-accent transition-transform duration-500 group-hover:rotate-12 md:col-span-1">{cap.n}</span>
              <div className="md:col-span-4">
                <h3 className="font-display text-3xl font-extrabold text-ink transition-colors duration-300 group-hover:text-accent md:text-4xl">{cap.title}</h3>
                <p className="mt-2 text-xs font-bold uppercase tracking-[0.14em] text-muted">{cap.tag}</p>
              </div>
              <p className="text-base leading-relaxed text-muted md:col-span-4">{cap.body}</p>
              <ul className="space-y-2 md:col-span-3">
                {cap.points.map((p) => {
                  const label = typeof p === "string" ? p : p.label;
                  return (
                    <li key={label} className="flex items-start gap-2 text-sm text-ink">
                      <Plus size={14} className="mt-1 shrink-0 text-accent" /> {label}
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          ))}
        </div>

        <div className="mt-8 sm:mt-10 md:mt-12">
          <Link
            to="/services"
            data-testid="capabilities-cta"
            className="inline-flex items-center gap-2 border border-ink px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-ink transition-colors hover:bg-ink hover:text-bone"
          >
            Explore all services
          </Link>
        </div>
      </div>
    </section>
  );
}
